import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
} from "@mui/material";
import { TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon } from "@mui/icons-material";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

interface Transaction {
  id: string;
  date: string | null;
  description: string;
  amount: number;
  type: "income" | "expense";
}

interface FinanceSummary {
  period_start: string;
  period_end: string;
  total_income: number;
  total_expense: number;
  profit: number;
  transactions: Transaction[];
  transactions_total: number;
}

const rub = (v: number) => `${Math.round(v).toLocaleString("ru-RU")} ₽`;

export default function FinancePage() {
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetch(`${API_BASE}/finance/summary`, { headers: getAuthHeaders() });
        if (resp.status === 403) throw new Error("Доступно только администратору");
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        setSummary(await resp.json());
      } catch (e: any) {
        console.error("Finance load error:", e);
        setError(e?.message || "Не удалось загрузить финансы");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const fmtDate = (iso: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Финансы
      </Typography>
      {summary && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Период: {fmtDate(summary.period_start)} — {fmtDate(summary.period_end)} (текущий месяц).
          Доходы — завершённые записи; расходы — выплаты мастерам.
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : summary ? (
        <>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TrendingUpIcon color="success" />
                    <Typography color="textSecondary" gutterBottom>
                      Доходы
                    </Typography>
                  </Box>
                  <Typography variant="h5" color="success.main">
                    {rub(summary.total_income)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <TrendingDownIcon color="error" />
                    <Typography color="textSecondary" gutterBottom>
                      Расходы
                    </Typography>
                  </Box>
                  <Typography variant="h5" color="error.main">
                    {rub(summary.total_expense)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Прибыль
                  </Typography>
                  <Typography variant="h5" color={summary.profit >= 0 ? "success.main" : "error.main"}>
                    {rub(summary.profit)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box mt={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Последние операции
                {summary.transactions_total > summary.transactions.length
                  ? ` (${summary.transactions.length} из ${summary.transactions_total})`
                  : ""}
              </Typography>
              {summary.transactions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  За период операций нет
                </Typography>
              ) : (
                <List>
                  {summary.transactions.map((t) => (
                    <ListItem key={t.id} divider>
                      <ListItemText
                        primary={t.description}
                        secondary={fmtDate(t.date)}
                      />
                      <Typography
                        variant="h6"
                        color={t.type === "income" ? "success.main" : "error.main"}
                      >
                        {t.type === "income" ? "+" : ""}
                        {Math.round(t.amount).toLocaleString("ru-RU")} ₽
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Box>
        </>
      ) : null}
    </Box>
  );
}
